/**
 * Real-time order status updates via Supabase Realtime (Uptime Kuma pattern).
 * Subscribes to Postgres changes on the orders table and pushes updates
 * to the UI instantly — no polling needed.
 */

import { supabase } from "./supabase";
import { emit, EVENTS } from "./event-bus";

export const ORDER_STATUSES = {
  PENDING_PAYMENT: "pending payment",
  PROCESSING: "processing",
  LABEL_CREATED: "label created",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

const STATUS_FLOW = [
  ORDER_STATUSES.PENDING_PAYMENT,
  ORDER_STATUSES.PROCESSING,
  ORDER_STATUSES.LABEL_CREATED,
  ORDER_STATUSES.SHIPPED,
  ORDER_STATUSES.DELIVERED,
];

export function getStatusStep(status) {
  const idx = STATUS_FLOW.indexOf(status);
  return idx === -1 ? -1 : idx;
}

export function isTerminalStatus(status) {
  return [ORDER_STATUSES.DELIVERED, ORDER_STATUSES.CANCELLED, ORDER_STATUSES.FAILED].includes(status);
}

/**
 * Subscribe to real-time order updates for a specific user.
 * Returns an unsubscribe function.
 *
 * @param {string} userId - The user's ID
 * @param {function} onUpdate - Called with (updatedOrder) when any order changes
 */
export function subscribeToOrderUpdates(userId, onUpdate) {
  if (!userId) return () => {};

  const channel = supabase
    .channel(`orders:user:${userId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "orders",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const updated = payload.new;
        onUpdate(updated);
        emit(EVENTS.ORDER_STATUS_CHANGED, {
          orderId: updated.id,
          status: updated.status,
          previousStatus: payload.old?.status,
          order: updated,
        });
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to real-time updates for a single order.
 * Useful for the post-checkout "tracking" view.
 * Auto-unsubscribes when the order reaches a terminal status.
 *
 * @param {string} orderId - The order ID to watch
 * @param {function} onUpdate - Called with (updatedOrder) on each change
 */
export function subscribeToSingleOrder(orderId, onUpdate) {
  if (!orderId) return () => {};

  let channel;

  const handler = (payload) => {
    const updated = payload.new;
    onUpdate(updated);

    if (isTerminalStatus(updated.status)) {
      supabase.removeChannel(channel);
    }
  };

  channel = supabase
    .channel(`order:${orderId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "orders",
        filter: `id=eq.${orderId}`,
      },
      handler
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
